import { escapeHtml as esc } from './richContent';

// Only editor-native nodes. No absolute positioning, generated text, pseudo
// elements, embedded documents or scripts. The saved output remains ordinary HTML.
const palettes = {
  moss: ['#edf0e3', '#284434', '#63764a'],
  ink: ['#243e35', '#fffbed', '#d2dcae'],
  rose: ['#f6e6df', '#573e3a', '#9a6353'],
  paper: ['#faf7ef', '#302f2a', '#797250'],
  blue: ['#e5edf2', '#263e52', '#537d95'],
  sun: ['#f3e5bb', '#493d22', '#876521'],
};
const p = (text) =>
  `<p style="font-size:17px;line-height:1.7;margin:12px 0">${esc(text)}</p>`;
const title = (text) =>
  `<h2 style="font-family:Georgia;font-size:36px;font-weight:400;line-height:1.2;margin:14px 0 20px">${esc(
    text
  )}</h2>`;
const kicker = (text) =>
  `<p style="font-size:11px;letter-spacing:2px;text-transform:uppercase;margin:0 0 16px">${esc(
    text
  )}</p>`;
const list = (items, ordered = false) =>
  `<${ordered ? 'ol' : 'ul'}>${items
    .map((text) => `<li>${p(text)}</li>`)
    .join('')}</${ordered ? 'ol' : 'ul'}>`;
const link = (text) =>
  `<p style="margin:24px 0"><a href="https://example.com" style="color:inherit;text-decoration:underline;font-weight:600">${esc(
    text
  )} ↗</a></p>`;
export function imagePlaceholder(tone = 'moss') {
  const [bg, ink, accent] = palettes[tone];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="480" viewBox="0 0 800 480"><rect width="800" height="480" fill="${bg}"/><circle cx="580" cy="135" r="65" fill="${accent}" opacity=".25"/><path d="M0 430L245 180L420 365L545 255L800 480H0Z" fill="${accent}" opacity=".18"/><text x="400" y="245" text-anchor="middle" fill="${ink}" font-family="Georgia,serif" font-size="32">Your image here</text><text x="400" y="285" text-anchor="middle" fill="${ink}" font-family="Arial,sans-serif" font-size="16">Select to replace</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
const image = (tone) =>
  `<p><img src="${imagePlaceholder(
    tone
  )}" alt="Replace with your own image" style="width:100%;height:auto;border-radius:12px" /></p>`;
const section = (name, body, tone = 'paper') => {
  const [bg, ink] = palettes[tone];
  return `<section data-section-label="${esc(
    name
  )}" style="background:${bg};color:${ink};padding:32px;border-radius:16px;margin:0 0 16px">${body}</section>`;
};
const columns = (left, right) =>
  `<div data-section-label="Two columns" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,220px),1fr));gap:20px"><section data-section-label="Left column" style="min-width:0">${left}</section><section data-section-label="Right column" style="min-width:0">${right}</section></div>`;
const hero = (eyebrow, headline, body, tone) =>
  section('Introduction', kicker(eyebrow) + title(headline) + p(body), tone);
const entry = (id, name, category, description, html) => ({
  id,
  name,
  category,
  description,
  html,
  native: true,
});

export const studioTemplates = [
  entry(
    'quiet-note',
    'A little note',
    'Personal',
    'A few words, beautifully left.',
    hero(
      'For you',
      'Some things are worth saying.',
      'Write the words you want someone to find.',
      'moss'
    ) + section('Signature', p('With love,') + p('Your name'))
  ),
  entry(
    'photo-memory',
    'Remember this?',
    'Personal',
    'One image. A story that stays.',
    section('Photo', image('rose'), 'rose') +
      hero(
        'A moment together',
        'Remember this day?',
        'Tell the story behind the photograph.',
        'paper'
      )
  ),
  entry(
    'love-letter',
    'Between us',
    'Personal',
    'An intimate letter in warm blush.',
    hero(
      'Only for you',
      'A little more than I usually say.',
      'Start your letter here.',
      'rose'
    ) +
      section(
        'Letter',
        p('The thing I remember most…') +
          p('And the thing I want you to know…') +
          p('Always, your name'),
        'paper'
      )
  ),
  entry(
    'travel',
    'A place worth keeping',
    'Places',
    'A travel journal with room for details.',
    section('Cover', kicker('Field notes · 01') + image('blue'), 'blue') +
      hero(
        'Somewhere special',
        'We were here.',
        'What did this place feel like?',
        'paper'
      ) +
      section(
        'Remember',
        list([
          'A sight worth remembering',
          'A sound you could still hear',
          'Something to come back for',
        ])
      )
  ),
  entry(
    'family',
    'For the next generation',
    'Personal',
    'A family memory and its meaning.',
    hero(
      'Our story',
      'Before you were here.',
      'A piece of our history, left for you.',
      'ink'
    ) +
      section(
        'Memory',
        image('paper') +
          title('This is where it began.') +
          p('Share the names, the little details, and why this moment matters.')
      )
  ),
  entry(
    'gratitude',
    'Thank you, really',
    'Personal',
    'A generous, simple thank-you.',
    hero(
      'A small note of gratitude',
      'You made a difference.',
      'Tell them what they did and what it meant to you.',
      'sun'
    ) + section('Signature', p('With appreciation,') + p('Your name'))
  ),
  entry(
    'invitation',
    'You’re invited',
    'Events',
    'An invitation with all the essentials.',
    hero(
      'Save a little time for us',
      'Let’s make a memory.',
      'Join us for something special.',
      'ink'
    ) +
      section(
        'The details',
        columns(
          title('When') + p('Saturday · 6:00 PM'),
          title('Where') + p('Venue name · Street address')
        ) + link('Let us know you’re coming')
      )
  ),
  entry(
    'agenda',
    'A day well spent',
    'Events',
    'A clear schedule for your gathering.',
    hero(
      'The gathering',
      'A day well spent.',
      'A little conversation. A little inspiration. A lot to take away.',
      'blue'
    ) +
      section(
        'Schedule',
        list(
          [
            '09:00 · Coffee & a warm welcome',
            '10:00 · Opening conversation',
            '12:00 · Lunch together',
            '14:00 · Ideas into action',
            '16:00 · Until next time',
          ],
          true
        )
      )
  ),
  entry(
    'speaker',
    'Meet the speaker',
    'Events',
    'A portrait, introduction, and session.',
    section(
      'Portrait',
      columns(
        image('moss'),
        kicker('In conversation') +
          title('Someone worth listening to.') +
          p('Name · Role · Organization')
      ),
      'moss'
    ) +
      section(
        'Session',
        title('The ideas we’ll explore.') +
          p('Introduce the talk in a few compelling sentences.') +
          link('Explore their work')
      )
  ),
  entry(
    'exhibition',
    'Behind the artwork',
    'Places',
    'Give an object its own voice.',
    section('Artwork', image('paper'), 'paper') +
      hero(
        'Artist · Year',
        'There’s a story behind this.',
        'Describe the artwork, the process, and the inspiration.',
        'ink'
      ) +
      section(
        'Credits',
        p('Materials · Collection · Artist name') + link('More from the artist')
      )
  ),
  entry(
    'welcome',
    'Make yourself at home',
    'Places',
    'A thoughtful welcome and local guide.',
    hero(
      'Welcome in',
      'Make yourself at home.',
      'Everything you need for a lovely stay.',
      'moss'
    ) +
      section(
        'Good to know',
        list([
          'Wi-Fi · Add network and password',
          'Check-out · Add time and instructions',
          'Need anything? · Add contact details',
        ])
      ) +
      section(
        'Around the corner',
        title('Our favorite little places.') +
          list([
            'Morning coffee · Your recommendation',
            'An evening walk · Your favorite route',
            'Dinner nearby · Somewhere special',
          ])
      )
  ),
  entry(
    'walk',
    'Take the scenic route',
    'Places',
    'An inviting guide to a neighborhood.',
    section('The view', image('blue'), 'blue') +
      hero(
        'A local’s guide',
        'Take the scenic route.',
        'An afternoon worth slowing down for.',
        'paper'
      ) +
      section(
        'Along the way',
        list(
          [
            'Start here · Introduce the first stop',
            'Look closer · A detail most people miss',
            'Stay awhile · Where to finish',
          ],
          true
        )
      )
  ),
  entry(
    'product',
    'Made with care',
    'Business',
    'A product spotlight with a clear next step.',
    section('Product', image('sun'), 'sun') +
      hero(
        'The latest from our studio',
        'Made with a little more care.',
        'Introduce your product and what makes it special.',
        'paper'
      ) +
      section(
        'Details',
        list([
          'The material · What it’s made of',
          'The craft · How it comes together',
          'The details · Sizes, colors, or options',
        ]) + link('Discover the collection')
      )
  ),
  entry(
    'offer',
    'Something just for you',
    'Business',
    'An offer without the noise.',
    hero(
      'A little thank-you for stopping by',
      'Your next favorite thing.',
      'Describe the offer in one clear sentence.',
      'ink'
    ) +
      section(
        'Offer details',
        title('Make it yours.') +
          p('Add the price, availability, and any conditions.') +
          link('Explore the offer'),
        'sun'
      )
  ),
  entry(
    'menu',
    'At our table',
    'Business',
    'A seasonal menu with editable dishes.',
    hero(
      'Seasonal & made with care',
      'At our table.',
      'Good food. Good company. Stay awhile.',
      'paper'
    ) +
      section(
        'To begin',
        title('Something small.') +
          list([
            'Dish name · Ingredients · Price',
            'Dish name · Ingredients · Price',
          ])
      ) +
      section(
        'The main event',
        title('Something to savor.') +
          list([
            'Dish name · Ingredients · Price',
            'Dish name · Ingredients · Price',
          ]),
        'moss'
      )
  ),
  entry(
    'contact',
    'Let’s stay connected',
    'Business',
    'A personal introduction and useful links.',
    section(
      'Introduction',
      columns(
        image('rose'),
        kicker('Hello, I’m') +
          title('Your name.') +
          p('What you do, and why you love it.')
      ),
      'rose'
    ) +
      section(
        'Connections',
        link('Visit my website') + link('See my work') + link('Get in touch')
      )
  ),
  entry(
    'announcement',
    'The next chapter',
    'Business',
    'A launch, update, or important announcement.',
    hero(
      'Something new is coming',
      'Our next chapter starts here.',
      'Share the news in your own words.',
      'blue'
    ) +
      section(
        'What’s next',
        title('Here’s what to know.') +
          list([
            'What is changing',
            'When it happens',
            'How to be part of it',
          ]) +
          link('Find out more')
      )
  ),
  entry(
    'guide',
    'A little know-how',
    'Learning',
    'Clear steps people can follow.',
    hero(
      'A practical guide',
      'You’ve got this.',
      'Explain what someone will learn or make.',
      'moss'
    ) +
      section(
        'Before you begin',
        list(['What you’ll need', 'How much time to allow'])
      ) +
      section(
        'The steps',
        list(
          [
            'Start with the essentials.',
            'Build on what you have.',
            'Finish with the little details.',
          ],
          true
        )
      )
  ),
  entry(
    'story',
    'The story behind it',
    'Learning',
    'An editorial article with an image and quote.',
    hero(
      'The journal · Story 01',
      'Look a little closer.',
      'A short introduction that invites someone to keep reading.',
      'paper'
    ) +
      section(
        'Story',
        image('moss') +
          title('Every detail has a beginning.') +
          p('Write the story here.')
      ) +
      section(
        'A thought to leave with',
        '<blockquote>' +
          p('The line you hope they remember.') +
          '</blockquote>',
        'moss'
      )
  ),
  entry(
    'community',
    'Better, together',
    'Community',
    'Bring people around a shared purpose.',
    hero(
      'An open invitation',
      'Good things happen together.',
      'Tell people what you’re working toward.',
      'sun'
    ) +
      section(
        'Our purpose',
        columns(
          title('Why it matters') + p('The difference we can make.'),
          title('How to help') + p('One simple thing someone can do.')
        )
      ) +
      section(
        'Join in',
        link('Be part of it') + p('Your organization · Contact details'),
        'ink'
      )
  ),
];

export const studioBlocks = [
  {
    name: 'Text section',
    detail: 'A heading and your words.',
    html: section('Text', title('A new thought.') + p('Start writing here.')),
  },
  {
    name: 'Image & caption',
    detail: 'Select the image to replace it.',
    html: section(
      'Image',
      image('moss') + p('Tell the story behind this image.')
    ),
  },
  {
    name: 'Two columns',
    detail: 'Two ideas, side by side.',
    html: section(
      'Columns',
      columns(
        title('One idea.') + p('Your words here.'),
        title('Another perspective.') + p('Your words here.')
      )
    ),
  },
  {
    name: 'Bullet list',
    detail: 'Add items with Enter.',
    html: section(
      'List',
      title('Good to know.') + list(['Your first point', 'Your next point'])
    ),
  },
  {
    name: 'Numbered steps',
    detail: 'A clear sequence.',
    html: section('Steps', list(['Start here', 'Continue here'], true)),
  },
  {
    name: 'Link',
    detail: 'An invitation to go further.',
    html: section('Link', link('Explore more')),
  },
];
